import React, { createContext, useContext, useState, useEffect } from 'react';
import { themesAPI } from '../lib/themesApi';

const ThemeContext = createContext(null);

export const useTheme = () => {
    const context = useContext(ThemeContext);
    if (!context) {
        throw new Error('useTheme must be used within a ThemeProvider');
    }
    return context;
};

const defaultThemeConfig = {
    primary_color: '#3b82f6',
    secondary_color: '#6366f1',
    accent_color: '#10b981',
    background_color: '#ffffff',
    text_color: '#1f2937',
    sidebar_style: 'light',
    header_style: 'default',
    border_radius: 'medium',
    logo_url: null,
    empresa_nombre: null,
};

export const ThemeProvider = ({ children }) => {
    const [theme, setTheme] = useState(() => {
        const savedTheme = localStorage.getItem('theme');
        return savedTheme || 'light';
    });

    const [themeConfig, setThemeConfig] = useState(defaultThemeConfig);
    const [themeLoading, setThemeLoading] = useState(false);

    useEffect(() => {
        const root = document.documentElement;
        
        if (theme === 'dark') {
            root.classList.add('dark');
        } else {
            root.classList.remove('dark');
        }
        
        localStorage.setItem('theme', theme);
    }, [theme]);

    // Cargar tema de la empresa cuando hay token
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (token) {
            loadCompanyTheme();
        }
    }, []);

    // Aplicar configuración de tema cuando cambia
    useEffect(() => {
        applyThemeToCSS(themeConfig);
    }, [themeConfig]);

    const loadCompanyTheme = async () => {
        try {
            setThemeLoading(true);
            const res = await themesAPI.getConfig();
            setThemeConfig({ ...defaultThemeConfig, ...res.data });
        } catch (err) {
            console.error('Error loading company theme:', err);
        } finally {
            setThemeLoading(false);
        }
    };

    const applyThemeToCSS = (config) => {
        const root = document.documentElement;
        
        // Colores personalizados
        root.style.setProperty('--theme-primary', config.primary_color);
        root.style.setProperty('--theme-secondary', config.secondary_color);
        root.style.setProperty('--theme-accent', config.accent_color);
        root.style.setProperty('--theme-background', config.background_color);
        root.style.setProperty('--theme-text', config.text_color);
        
        // Convertir hex a HSL para Tailwind/Shadcn
        const primaryHSL = hexToHSL(config.primary_color);
        if (primaryHSL) {
            root.style.setProperty('--primary', primaryHSL);
        }
        
        // Border radius
        const radiusMap = {
            'none': '0',
            'small': '0.25rem',
            'medium': '0.5rem',
            'large': '1rem'
        };
        root.style.setProperty('--radius', radiusMap[config.border_radius] || '0.5rem');
    };

    const updateTheme = async (newConfig) => {
        try {
            const res = await themesAPI.updateConfig(newConfig);
            setThemeConfig({ ...themeConfig, ...res.data });
            return res.data;
        } catch (err) {
            throw err;
        }
    };

    const refreshTheme = () => {
        loadCompanyTheme();
    };

    const toggleTheme = () => {
        setTheme(prev => prev === 'light' ? 'dark' : 'light');
    };

    const setDarkMode = () => setTheme('dark');
    const setLightMode = () => setTheme('light');

    const value = {
        // Dark/Light mode
        theme,
        setTheme,
        toggleTheme,
        setDarkMode,
        setLightMode,
        isDark: theme === 'dark',
        // Company theme
        themeConfig,
        setThemeConfig,
        updateTheme,
        refreshTheme,
        themeLoading,
        defaultThemeConfig,
        // Helpers
        primaryColor: themeConfig.primary_color,
    };

    return (
        <ThemeContext.Provider value={value}>
            {children}
        </ThemeContext.Provider>
    );
};

// Helper function to convert hex to HSL
function hexToHSL(hex) {
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    if (!result) return '24 95% 53%';
    
    let r = parseInt(result[1], 16) / 255;
    let g = parseInt(result[2], 16) / 255;
    let b = parseInt(result[3], 16) / 255;
    
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h, s, l = (max + min) / 2;
    
    if (max === min) {
        h = s = 0;
    } else {
        const d = max - min;
        s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
        switch (max) {
            case r: h = ((g - b) / d + (g < b ? 6 : 0)) / 6; break;
            case g: h = ((b - r) / d + 2) / 6; break;
            case b: h = ((r - g) / d + 4) / 6; break;
            default: h = 0;
        }
    }
    
    return `${Math.round(h * 360)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
}

export default ThemeContext;
